import React, { useState } from "react";
import { Phone, Cpu, CreditCard, TrendingUp, Zap } from "lucide-react";
import useApiEp from "../../hooks/useApiEp";
import { useParams } from "react-router-dom";
import MuPb from "../../widgets/MuPb";
import { Alert } from "@mui/material";
import { getErrorResponse } from "../../utils/util";

// Color variants for businesses
const colorVariants = [
  "#e91e63", // Pink
  "#00bcd4", // Cyan
  "#3f51b5", // Indigo
  "#4caf50", // Green
  "#ff9800", // Orange
  "#9c27b0", // Purple
  "#2196f3", // Blue
  "#f06292", // Light Pink
  "#009688", // Teal
  "#ff5722", // Deep Orange
];

// Function to generate consistent color based on client ID
const getClientColor = (clientId, index) => {
  if (!clientId) return colorVariants[index % colorVariants.length];

  // Generate consistent color based on clientId string
  let hash = 0;
  for (let i = 0; i < clientId.length; i++) {
    hash = clientId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colorVariants[Math.abs(hash) % colorVariants.length];
};

export default function RechargeHub() {
  const [activeTab, setActiveTab] = useState(0);

  const { id } = useParams();
  const { data, isLoading, isError, error } = useApiEp(
    `/user/get-clients/${id}`
  );

  const handleRecharge = (clientId, type) => {
    window.location.href = `/${clientId}/reseller-recharge`;
  };

  const getInitials = (name) => {
    if (!name) return "CL";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const rechargeType = activeTab === 0 ? "waba" : "ai";

  if (isLoading) {
    return <MuPb />;
  }
  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  // Transform API data to match the expected format
  const clients =
    data?.map((client, index) => ({
      id: client._id,
      name: client.customerId || "Unknown Client",
      color: getClientColor(client._id, index),
    })) || [];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: "32px 16px",
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              marginBottom: "12px",
            }}
          >
            <Zap size={40} color="#ffd700" />
            <h1
              style={{
                fontSize: "3rem",
                fontWeight: 700,
                color: "white",
                margin: 0,
                textShadow: "2px 2px 4px rgba(0,0,0,0.2)",
              }}
            >
              Client Recharge Hub
            </h1>
          </div>
          <p
            style={{
              fontSize: "1.25rem",
              color: "rgba(255,255,255,0.9)",
              margin: 0,
            }}
          >
            Manage your client credits seamlessly
          </p>
        </div>

        {/* Main Card */}
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            overflow: "hidden",
          }}
        >
          {/* Tabs */}
          <div
            style={{
              display: "flex",
              borderBottom: "1px solid #e0e0e0",
              background: "#f5f5f5",
            }}
          >
            <button
              onClick={() => setActiveTab(0)}
              style={{
                flex: 1,
                padding: "20px 24px",
                fontSize: "1.125rem",
                fontWeight: 600,
                border: "none",
                background: activeTab === 0 ? "white" : "transparent",
                color: activeTab === 0 ? "#667eea" : "#666",
                borderBottom: activeTab === 0 ? "4px solid #667eea" : "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                transition: "all 0.2s",
              }}
            >
              <Phone size={24} />
              WhatsApp Credits
            </button>
            <button
              onClick={() => setActiveTab(1)}
              style={{
                flex: 1,
                padding: "20px 24px",
                fontSize: "1.125rem",
                fontWeight: 600,
                border: "none",
                background: activeTab === 1 ? "white" : "transparent",
                color: activeTab === 1 ? "#667eea" : "#666",
                borderBottom: activeTab === 1 ? "4px solid #667eea" : "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                transition: "all 0.2s",
              }}
            >
              <Cpu size={24} />
              AI Credits
            </button>
          </div>

          {/* Content */}
          <div style={{ padding: "32px" }}>
            {/* Info Bar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                marginBottom: "24px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 16px",
                  background: "#ede7f6",
                  color: "#5e35b1",
                  borderRadius: "24px",
                  fontWeight: 600,
                }}
              >
                <TrendingUp size={20} />
                {activeTab === 0 ? "WhatsApp Business API" : "AI Assistant"}
              </div>
              <span style={{ color: "#666" }}>
                {clients.length} active{" "}
                {clients.length === 1 ? "client" : "clients"}
              </span>
            </div>

            {/* Client Cards Grid */}
            {clients.length > 0 ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                  gap: "24px",
                }}
              >
                {clients.map((client) => (
                  <div
                    key={client.id}
                    style={{
                      background:
                        "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                      borderRadius: "12px",
                      border: "1px solid #e0e0e0",
                      padding: "24px",
                      textAlign: "center",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-8px)";
                      e.currentTarget.style.boxShadow =
                        "0 12px 24px rgba(0,0,0,0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    {/* Avatar */}
                    <div
                      style={{
                        width: "64px",
                        height: "64px",
                        borderRadius: "50%",
                        background: client.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 16px",
                        color: "white",
                        fontSize: "1.5rem",
                        fontWeight: "bold",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      }}
                    >
                      {getInitials(client.name)}
                    </div>

                    {/* Client Name */}
                    <h3
                      style={{
                        fontSize: "1.125rem",
                        fontWeight: 600,
                        color: "#333",
                        margin: "0 0 16px 0",
                        minHeight: "48px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {client.name}
                    </h3>

                    {/* Recharge Button */}
                    <button
                      onClick={() => handleRecharge(client.name, rechargeType)}
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        background: client.color,
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "1rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.05)";
                        e.currentTarget.style.boxShadow =
                          "0 6px 16px rgba(0,0,0,0.2)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      <CreditCard size={20} />
                      Recharge Now
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  color: "#666",
                }}
              >
                <p style={{ fontSize: "1.125rem", margin: 0 }}>
                  No clients found. Start by adding your first client.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer Stats */}
        <div
          style={{
            marginTop: "32px",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "16px",
          }}
        >
          <div
            style={{
              background: "rgba(255,255,255,0.1)",
              backdropFilter: "blur(10px)",
              borderRadius: "12px",
              padding: "24px",
              textAlign: "center",
            }}
          >
            <div
              style={{ fontSize: "2rem", fontWeight: "bold", color: "white" }}
            >
              {clients.length}
            </div>
            <div style={{ color: "rgba(255,255,255,0.8)" }}>Total Clients</div>
          </div>
          <div
            style={{
              background: "rgba(255,255,255,0.1)",
              backdropFilter: "blur(10px)",
              borderRadius: "12px",
              padding: "24px",
              textAlign: "center",
            }}
          >
            <div
              style={{ fontSize: "2rem", fontWeight: "bold", color: "white" }}
            >
              2
            </div>
            <div style={{ color: "rgba(255,255,255,0.8)" }}>Credit Types</div>
          </div>
          <div
            style={{
              background: "rgba(255,255,255,0.1)",
              backdropFilter: "blur(10px)",
              borderRadius: "12px",
              padding: "24px",
              textAlign: "center",
            }}
          >
            <div
              style={{ fontSize: "2rem", fontWeight: "bold", color: "white" }}
            >
              100%
            </div>
            <div style={{ color: "rgba(255,255,255,0.8)" }}>Uptime</div>
          </div>
        </div>
      </div>
    </div>
  );
}
